import RefreshTwoToneIcon from "@mui/icons-material/RefreshTwoTone";
import RestartAltTwoToneIcon from "@mui/icons-material/RestartAltTwoTone";
import SaveTwoToneIcon from "@mui/icons-material/SaveTwoTone";
import SmartToyTwoToneIcon from "@mui/icons-material/SmartToyTwoTone";
import {
	Alert,
	alpha,
	Box,
	Button,
	Card,
	CardContent,
	CardHeader,
	CircularProgress,
	Container,
	IconButton,
	TextField,
	Typography,
	useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import GlobalLoader from "src/components/SuspenseLoader/GlobalLoader";
import { useApiMutation } from "src/hooks/useApiMutation";
import { useApiQuery } from "src/hooks/useApiQuery";
import type {
	IChatbotKnowledgeBase,
	IChatbotKnowledgeBasePayload,
} from "src/Types/module/chatbot/chatbotKnowledgeBase.types";

const ChatbotKnowledgeBase: React.FC = () => {
	const theme = useTheme();
	const [textContent, setTextContent] = useState<string>("");
	const [formError, setFormError] = useState<string | null>(null);

	// Fetch current Knowledge Base configuration
	const {
		data: rawData,
		isLoading,
		isFetching,
		refetch,
	} = useApiQuery<IChatbotKnowledgeBase>({
		endPoint: "/api/v1/admin/chatbot/knowledge-base",
		queryKeys: ["ChatbotKnowledgeBase"],
	});

	// Normalize data from response (handles both wrapped in .data and direct payload)
	const config: IChatbotKnowledgeBase | null = useMemo(() => {
		if (!rawData) return null;
		if (typeof rawData === "string") {
			return { text_content: rawData };
		}
		if ((rawData as any)?.data?.text_content !== undefined) {
			return (rawData as any).data;
		}
		if (rawData?.text_content !== undefined) {
			return rawData;
		}
		return null;
	}, [rawData]);

	// Sync fetched config into form state
	useEffect(() => {
		if (config && typeof config.text_content === "string") {
			setTextContent(config.text_content);
		}
	}, [config]);

	// Mutation to update Knowledge Base
	const { mutate: updateKnowledgeBase, isPending } = useApiMutation<
		IChatbotKnowledgeBasePayload,
		any
	>({
		method: "POST",
		endPoint: "/api/v1/admin/chatbot/knowledge-base",
		invalidateQueries: ["ChatbotKnowledgeBase"],
	});

	const isModified = textContent !== (config?.text_content ?? "");

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		setFormError(null);

		if (!textContent.trim()) {
			setFormError("Please enter knowledge base content.");
			return;
		}

		updateKnowledgeBase(
			{
				text_content: textContent,
			},
			{
				onError: (err: any) => {
					console.error("Failed to update knowledge base:", err);
					setFormError(err?.message || "Failed to update configuration.");
				},
			}
		);
	};

	const handleReset = () => {
		setTextContent(config?.text_content || "");
		setFormError(null);
	};

	if (isLoading && !config) {
		return <GlobalLoader />;
	}

	const lastUpdated = config?.updated_at || config?.created_at;

	return (
		<>
			<Helmet>
				<title>BTTF - Chatbot Knowledge Base</title>
			</Helmet>

			<Container maxWidth="lg" sx={{ mt: 4, mb: 4 }} translate="no" className="notranslate">
				<Card elevation={2} sx={{ borderRadius: 2 }}>
					<CardHeader
						avatar={
							<Box
								sx={{
									p: 1,
									borderRadius: 1.5,
									bgcolor: alpha(theme.palette.primary.main, 0.1),
									color: theme.palette.primary.main,
									display: "flex",
								}}
							>
								<SmartToyTwoToneIcon fontSize="medium" />
							</Box>
						}
						action={
							<IconButton
								onClick={() => refetch()}
								disabled={isFetching || isPending}
								size="small"
								title="Refresh from server"
								sx={{ mt: 1, mr: 1 }}
							>
								<RefreshTwoToneIcon fontSize="small" />
							</IconButton>
						}
						title={
							<Typography variant="h4" fontWeight={700}>
								<span>Chatbot Knowledge Base Configuration</span>
							</Typography>
						}
						subheader={
							<span>
								{lastUpdated
									? `Last updated: ${dayjs(lastUpdated).format("DD MMM, YYYY hh:mm A")}`
									: "Configure guidelines, event schedules, special rules, and FAQs for the AI chatbot"}
							</span>
						}
						sx={{ p: 2.5, borderBottom: "1px solid rgba(0,0,0,0.08)" }}
					/>

					<CardContent sx={{ p: 3 }}>
						{formError && (
							<Alert
								severity="error"
								sx={{ mb: 2.5, borderRadius: 1.5 }}
								onClose={() => setFormError(null)}
							>
								<span>{formError}</span>
							</Alert>
						)}

						<Box component="form" onSubmit={handleSubmit}>
							<Box sx={{ mb: 3 }}>
								<Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
									<span>Knowledge Base Content (text_content)</span>
								</Typography>
								<TextField
									fullWidth
									multiline
									rows={14}
									placeholder="Enter BTTF Fair guidelines, event schedules, special rules, and FAQ details..."
									value={textContent}
									onChange={(e) => {
										setTextContent(e.target.value);
										if (formError) setFormError(null);
									}}
									helperText="The AI chatbot uses this text as its core knowledge to answer user queries."
									sx={{
										"& textarea": {
											resize: "vertical",
											fontFamily: "inherit",
											lineHeight: 1.6,
										},
									}}
								/>
							</Box>

							{/* Actions */}
							<Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
								<Button
									variant="outlined"
									color="secondary"
									startIcon={<RestartAltTwoToneIcon />}
									onClick={handleReset}
									disabled={!isModified || isPending}
								>
									<span>Reset</span>
								</Button>

								<Button
									type="submit"
									variant="contained"
									color="primary"
									disabled={!textContent.trim() || !isModified || isPending}
									startIcon={
										isPending ? (
											<CircularProgress size={16} color="inherit" />
										) : (
											<SaveTwoToneIcon />
										)
									}
								>
									<span>{isPending ? "Saving..." : "Save Configuration"}</span>
								</Button>
							</Box>
						</Box>
					</CardContent>
				</Card>
			</Container>
		</>
	);
};

export default ChatbotKnowledgeBase;
